// COP — MARKER-AUTHOR capability (S3): the operator drops a typed point on the map.
//
// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
// references CONTRACT:P1-CANONICAL-ENVELOPE.1.0 (the authored body shape)
//         + CONTRACT:S1-TAK-GATEWAY.1.0 (the /emit egress crossing).
//
// THE SHAPE: this module is two PURE, T0-testable builders + nothing else. It holds NO
// MapLibre, NO engine, NO bus, NO React — the host (cop.ts) owns the map-click → coordinate
// path and the publish/egress seams, and calls these builders to MINT one CanonicalEnvelope
// for an operator-authored waypoint. Keeping the security-critical envelope construction here
// (a) makes it unit-testable standalone (markerCapability.test.ts), and (b) keeps the seam a
// single auditable place rather than scattered inline in a 2000-line host.
//
// FAIL-CLOSED CLASSIFICATION (the load-bearing security invariant): an operator-authored
// object MUST carry a VALID, KNOWN classification. We NEVER default-to-permissive (no silent
// "UNCLASSIFIED" fallback that could leak a higher-cls intent into a lower marking, and no
// "TOP_SECRET" fallback that over-classifies into nothing). An absent / unrecognised pick is
// REFUSED — `resolveMarkerClassification` returns null and the host drops the placement with a
// visible error (feedback-fail-closed-on-malformed-security-data). Marking is the operator's
// CURRENT working pick; releasability/mission/caveats start EMPTY (the operator names a
// point — they do not silently inherit another object's caveats).
//
// PROJECTION-COHERENCE (I12) + NO-BYPASS (I13): this module returns an envelope; it does NOT
// touch the engine. The host publishes it through the SAME `HostContext.data.publish` → bus →
// pdp.record → projection delta → sole-host-writer paint path every other object rides, so the
// authored marker is gated + painted identically (it can even be self-denied if the operator
// authors above their own down-filtered view — the projection drops it, Glomar-clean).

import type { CanonicalEnvelope, SecurityLabels } from "../../../../packages/schema/ts/envelope.ts";

/** The canonical classification ladder an authored marker may carry. INDEPENDENT of the host's
 *  private CLASS_ORDER (C3: a capability touches only contract types) — this is the fail-closed
 *  allow-list: a pick NOT in this set is refused, never coerced. Kept in lock-step with cop.ts's
 *  LEVELS by the shared canonical DoD ladder (UNCLASSIFIED…TOP_SECRET). */
export const MARKER_CLASSIFICATIONS = [
  "UNCLASSIFIED",
  "CUI",
  "CONFIDENTIAL",
  "SECRET",
  "TOP_SECRET",
] as const;

export type MarkerClassification = (typeof MARKER_CLASSIFICATIONS)[number];

/** FAIL-CLOSED classification resolver. The operator's CURRENT pick (their working/query level)
 *  is the ONLY source — there is NO default. Returns the validated canonical level, or `null`
 *  when the pick is absent / blank / unrecognised. The host MUST treat `null` as a hard refusal
 *  (do not publish), never as a permissive fallback. Normalises case + whitespace + a couple of
 *  common spellings ("TOP SECRET" → "TOP_SECRET") so a legitimate pick is not rejected on form. */
export function resolveMarkerClassification(pick: string | null | undefined): MarkerClassification | null {
  if (typeof pick !== "string") return null;
  const norm = pick.trim().toUpperCase().replace(/[\s-]+/g, "_");
  return (MARKER_CLASSIFICATIONS as readonly string[]).includes(norm)
    ? (norm as MarkerClassification)
    : null;
}

/** Input the host hands the builder for one operator-authored waypoint. */
export interface MarkerInput {
  /** Longitude (GeoJSON order is [lon, lat]). */
  lon: number;
  /** Latitude. */
  lat: number;
  /** The ALREADY-RESOLVED, validated classification (caller used resolveMarkerClassification
   *  and refused on null). Re-validated here defensively — the builder is the last gate. */
  classification: MarkerClassification;
  /** Operator-legible label for the point (e.g. "WP-1"). */
  label: string;
  /** The authoring persona's short code (provenance + properties.source). */
  authorShort: string;
  /** ISO timestamp; the host supplies it so the function stays pure/deterministic in tests. */
  nowIso: string;
  /** Stable id; the host supplies it (so the function is deterministic + testable). */
  id: string;
  /** Optional stale-after ISO (defaults to nowIso when absent). */
  staleIso?: string;
  /** Optional policy ref to stamp (defaults to a marker policy uri). */
  policyRef?: string;
}

/** Thrown when the builder is handed an invalid classification — the LAST fail-closed gate.
 *  The host already refuses on a null resolve; this guards against a caller bypassing the
 *  resolver. A throw is the right shape: a malformed security label must never produce an
 *  envelope (it would be worse to mint a mismarked object than to drop the placement). */
export class MarkerClassificationError extends Error {
  constructor(pick: unknown) {
    super(`marker: refused — classification "${String(pick)}" is not a known level (fail-closed)`);
    this.name = "MarkerClassificationError";
  }
}

/**
 * buildMarkerEnvelope — mint ONE CanonicalEnvelope for an operator-authored waypoint.
 *
 * PURE + DETERMINISTIC (all non-determinism — id, time — is injected). Mirrors the existing
 * envelope shape (apps/cop/src/scenarios/hormuz.ts): a clear-shell `geometry` Point. The body
 * is CLEAR (operator-authored intent, no sealed payload) but the SECURITY marking is the
 * operator's real working classification — so the projection/banner gate + mark it honestly.
 *
 * FAIL-CLOSED: re-validates the classification and THROWS MarkerClassificationError on a bad
 * one (never silently normalises to a permissive default).
 */
export function buildMarkerEnvelope(input: MarkerInput): CanonicalEnvelope {
  const cls = resolveMarkerClassification(input.classification);
  if (cls === null) throw new MarkerClassificationError(input.classification);

  const security: SecurityLabels = {
    classification: cls,
    // The operator names a POINT — it does NOT silently inherit another object's releasability,
    // mission, or caveats. Empty = "as marked, no extra dissemination control authored here".
    releasability: [],
    mission: [],
    caveats: [],
    originator_control: false,
    policy_ref: input.policyRef ?? "policy://cop/operator-marker",
    // Clear-shell: the body is operator intent, not a sealed payload. payload_mode=clear,
    // leakage_profile=clear so the projection treats it as a fully-released visible object.
    payload_mode: "clear",
    leakage_profile: "clear",
  };

  return {
    id: input.id,
    schema: "scop.envelope/1.0",
    kind: "geometry",
    time: input.nowIso,
    valid_time: { start: input.nowIso, stale: input.staleIso ?? input.nowIso },
    geometry: { type: "Point", coordinates: [input.lon, input.lat] },
    properties: {
      name: input.label,
      affiliation: "friend",
      type: "waypoint",
      // The authoring marker is operator-placed; tag it so the inspector/audit reads who.
      source: `cop-marker/${input.authorShort}`,
      author: input.authorShort,
      marker: true,
    },
    security,
    provenance: {
      originator: input.authorShort,
      adapter: "cop/operator-marker",
      received_at: input.nowIso,
      chain: [{ adapter: "cop/operator-marker", at: input.nowIso, note: "operator-authored waypoint" }],
    },
    // Clear body — the operator's authored point carries no sealed inner. The egress path
    // (S1-TAK-GATEWAY) bridges this clear body as the marked CoT placeholder.
    payload: { clear: { kind: "waypoint", label: input.label, lon: input.lon, lat: input.lat } },
  };
}
